import type {
	CoursesPostDiscussionReplyDto,
	CoursesSetDiscussionClosedDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import { invalidatesAfterDelete } from '../../utils/courses-errors.util';
import type {
	GetDiscussionInput,
	GetDiscussionOutput,
	PostDiscussionReplyInput,
	PostDiscussionReplyOutput,
	AdminDeleteDiscussionReplyInput,
	AdminDeleteDiscussionReplyOutput,
	SetDiscussionClosedInput,
	SetDiscussionClosedOutput,
} from '../../types/discussions.types';

const TAG = 'courses:discussions' as const;
const INVALIDATES = [TAG, 'courses:community'] as const;

export const discussionsEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		getDiscussion: builder.query<GetDiscussionOutput, GetDiscussionInput>({
			query: ({ id }) => ({ url: `courses/community/discussions/${encodeURIComponent(id)}` }),
			providesTags: [TAG],
		}),
		postDiscussionReply: builder.mutation<PostDiscussionReplyOutput, PostDiscussionReplyInput>({
			query: ({ discussionId, ...body }) => ({
				url: `courses/community/discussions/${encodeURIComponent(discussionId)}/replies`,
				method: 'POST',
				body: body satisfies Wire<CoursesPostDiscussionReplyDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminDeleteDiscussionReply: builder.mutation<
			AdminDeleteDiscussionReplyOutput,
			AdminDeleteDiscussionReplyInput
		>({
			query: ({ replyId }) => ({
				url: `courses/community/discussion-replies/${encodeURIComponent(replyId)}`,
				method: 'DELETE',
			}),
			invalidatesTags: invalidatesAfterDelete(INVALIDATES),
		}),
		setDiscussionClosed: builder.mutation<SetDiscussionClosedOutput, SetDiscussionClosedInput>({
			query: ({ discussionId, ...body }) => ({
				url: `courses/community/discussions/${encodeURIComponent(discussionId)}/closed`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesSetDiscussionClosedDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useGetDiscussionQuery,
	useLazyGetDiscussionQuery,
	usePostDiscussionReplyMutation,
	useAdminDeleteDiscussionReplyMutation,
	useSetDiscussionClosedMutation,
} = discussionsEndpoints;
